import { isNil } from 'lodash-es';
import type {
  QuantzDataDto,
  QuantzMetaDto,
  QuantzDataItemDto,
  SuccessQuantzResponseDto,
} from './dto';
import type {
  QuantzCategoryDataItem,
  QuantzData,
  QuantzDateDataItem,
  QuantzMeta,
  QuantzResponse,
} from '../model';
import { QuantzResponseKind, QuantzOperation } from '../model';

export function mapMetaDtoToModel(dto: QuantzMetaDto<true>): QuantzMeta<true> {
  return {
    operation: dto.operation,
    operations: dto.operations,
    success: dto.success,
    windowed: dto.windowed,
    level: dto.level,
    resolution: dto?.resolution,
  };
}

function mapDateValueDtoToModel(dto: QuantzDataItemDto[]): QuantzDateDataItem[] {
  return dto.map((oneTupleValue) => ({
    date: new Date((oneTupleValue[0] as number) * 1000),
    value: oneTupleValue[1],
  }));
}

function mapCategoryValueDtoToModel(dto: QuantzDataItemDto[]): QuantzCategoryDataItem[] {
  return dto.map((oneTupleValue) => ({
    category: `${oneTupleValue[0]}`,
    value: oneTupleValue[1] ?? 0,
  }));
}

function mapAverageFeedbackValueDtoToModel(dto: QuantzDataItemDto[]): QuantzDateDataItem[] {
  return dto.map((oneTupleValue) => ({
    date: new Date((oneTupleValue[0] as number) * 1000),
    value: oneTupleValue[1] !== null ? oneTupleValue[1] + 1 : null,
  }));
}

function mapByDateDataDtoToModel(dto: QuantzDataDto): QuantzData<QuantzDateDataItem> {
  if (dto.result_series) {
    return {
      resultSeries: dto.result_series.map((oneSeries) => ({
        key: `${oneSeries.key}`,
        values: mapDateValueDtoToModel(oneSeries.values),
      })),
      totalSeries: dto.total_series ? mapDateValueDtoToModel(dto.total_series) : undefined,
      total: dto.total ?? undefined,
    };
  }
  return {
    result: dto.result,
    totalSeries: dto.total_series ? mapDateValueDtoToModel(dto.total_series) : undefined,
    total: dto.total ?? undefined,
  };
}

function mapByCategoryDataDtoToModel(dto: QuantzDataDto): QuantzData<QuantzCategoryDataItem> {
  if (dto.result_series) {
    return {
      resultSeries: dto.result_series.map((oneSeries) => ({
        key: `${oneSeries.key}`,
        values: mapCategoryValueDtoToModel(oneSeries.values),
      })),
      total: dto.total ?? undefined,
    };
  }
  return {
    result: dto.result,
    total: dto.total ?? undefined,
  };
}

function mapFeedbackByCategoryDataDtoToModel(
  dto: QuantzDataDto,
): QuantzData<QuantzCategoryDataItem> {
  if (dto.result_series) {
    return {
      resultSeries: dto.result_series.map((oneSeries) => ({
        key: `${oneSeries.key}`,
        values: mapCategoryValueDtoToModel(oneSeries.values),
      })),
      total: dto.total !== undefined ? dto.total : undefined,
    };
  }
  return {
    result: dto.result,
    total: dto.total ?? undefined,
  };
}

function mapFeedbackByDateDataDataDtoToModel(dto: QuantzDataDto): QuantzData<QuantzDateDataItem> {
  if (dto.result_series) {
    return {
      resultSeries: dto.result_series.map((oneSeries) => ({
        key: `${oneSeries.key}`,
        values: mapAverageFeedbackValueDtoToModel(oneSeries.values),
      })),
      total: dto.total !== undefined ? dto.total + 1 : undefined,
    };
  }
  return {
    result: dto.result,
    total: dto.total ?? undefined,
  };
}

export function mapDtoToModel(responseDto: SuccessQuantzResponseDto): QuantzResponse {
  const meta = mapMetaDtoToModel(responseDto.meta);

  const kind = responseDto.meta.windowed
    ? QuantzResponseKind.BY_DATE
    : QuantzResponseKind.BY_CATEGORY;

  switch (kind) {
    case QuantzResponseKind.BY_DATE: {
      return {
        kind,
        meta,
        data: mapByDateDataDtoToModel(responseDto.data),
      };
    }
    case QuantzResponseKind.BY_CATEGORY: {
      return {
        kind,
        meta,
        data: mapByCategoryDataDtoToModel(responseDto.data),
      };
    }
    default:
      throw new Error('Unknown type');
  }
}

export function mapFeedbackDtoToModel(responseDto: SuccessQuantzResponseDto): QuantzResponse {
  const meta = mapMetaDtoToModel(responseDto.meta);

  if (meta.operation === QuantzOperation.COUNT) {
    return {
      kind: QuantzResponseKind.BY_CATEGORY,
      meta,
      data: mapFeedbackByCategoryDataDtoToModel(responseDto.data),
    };
  }

  return {
    kind: QuantzResponseKind.BY_DATE,
    meta,
    data: mapFeedbackByDateDataDataDtoToModel(responseDto.data),
  };
}

// The backend may return a category with an empty string value ''.
// The backend returnins up to 6 (CLICKED_ELEMENT_LIMIT) categories.
//
// The function removes the empty category for users.
// If there is no empty string category and there are 7 categories, the last category must be removed, as the UI has a limit of 6 categories per chart.
export function mapClickedElementDtoToModel(
  responseDto: SuccessQuantzResponseDto,
  limit?: number,
): QuantzResponse {
  const meta = mapMetaDtoToModel(responseDto.meta);

  const kind = responseDto.meta.windowed
    ? QuantzResponseKind.BY_DATE
    : QuantzResponseKind.BY_CATEGORY;

  switch (kind) {
    case QuantzResponseKind.BY_DATE: {
      const emptyStringValueCategoryIndex = responseDto.data.result_series?.findIndex(
        (series) => series.key === '',
      );
      if (emptyStringValueCategoryIndex !== undefined && emptyStringValueCategoryIndex >= 0) {
        responseDto.data.result_series?.splice(emptyStringValueCategoryIndex, 1);
      } else if (!isNil(limit) && responseDto.data.result_series?.length === limit) {
        responseDto.data.result_series?.splice(-1);
      }

      return {
        kind,
        meta,
        data: mapByDateDataDtoToModel(responseDto.data),
      };
    }
    case QuantzResponseKind.BY_CATEGORY: {
      const emptyStringValueCategoryIndex = responseDto.data.result_series?.[0].values.findIndex(
        (series) => series[0] === '',
      );
      if (emptyStringValueCategoryIndex !== undefined && emptyStringValueCategoryIndex >= 0) {
        responseDto.data.result_series?.[0].values.splice(emptyStringValueCategoryIndex, 1);
      } else if (!isNil(limit) && responseDto.data.result_series?.[0].values.length === limit) {
        responseDto.data.result_series?.[0].values.splice(-1);
      }

      return {
        kind,
        meta,
        data: mapByCategoryDataDtoToModel(responseDto.data),
      };
    }
    default:
      throw new Error('Unknown type');
  }
}
